import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, CheckCircle2, XCircle, Plus, Trash2, BookOpen } from 'lucide-react';
import { useVerificacaoEficaciaFormulario } from '@/hooks/useVerificacaoEficaciaFormulario';
import { useLicoesAprendidasFormulario } from '@/hooks/useLicoesAprendidasFormulario';
import { useAuth } from '@/hooks/useAuth';

interface TabEncerramentoProps {
  planoAcaoId: string;
  readOnly?: boolean;
}

const TIPO_LICAO_LABELS: Record<string, string> = {
  sucesso: 'Sucesso',
  melhoria: 'Melhoria',
  falha: 'Falha',
};

const TIPO_LICAO_COLORS: Record<string, string> = {
  sucesso: 'bg-green-100 text-green-800 border-green-200',
  melhoria: 'bg-blue-100 text-blue-800 border-blue-200',
  falha: 'bg-red-100 text-red-800 border-red-200',
};

export function TabEncerramento({ planoAcaoId, readOnly = false }: TabEncerramentoProps) {
  const { user } = useAuth();
  
  // Verificação de Eficácia
  const { verificacao, isLoading: isLoadingVerificacao, saveVerificacao, isSaving: isSavingVerificacao } = useVerificacaoEficaciaFormulario(planoAcaoId);
  const [eficaz, setEficaz] = useState<boolean | null>(null);
  const [criteriosAvaliacao, setCriteriosAvaliacao] = useState('');
  const [resultadoObtido, setResultadoObtido] = useState('');
  const [acoesAdicionais, setAcoesAdicionais] = useState('');
  const [observacoesVerificacao, setObservacoesVerificacao] = useState('');

  // Lições Aprendidas
  const { licoes, isLoading: isLoadingLicoes, createLicao, deleteLicao, isCreating } = useLicoesAprendidasFormulario(planoAcaoId);
  const [novaLicao, setNovaLicao] = useState({
    tipo: 'melhoria' as 'sucesso' | 'melhoria' | 'falha',
    titulo: '',
    descricao: '',
    impacto: '',
    recomendacoes: '',
  });
  const [showNovaLicaoForm, setShowNovaLicaoForm] = useState(false);

  // Load verificacao data
  useEffect(() => {
    if (verificacao) {
      setEficaz(verificacao.eficaz ?? null);
      setCriteriosAvaliacao(verificacao.criterios_avaliacao || '');
      setResultadoObtido(verificacao.resultado_obtido || '');
      setAcoesAdicionais(verificacao.acoes_adicionais || '');
      setObservacoesVerificacao(verificacao.observacoes || '');
    }
  }, [verificacao]);

  const handleSaveVerificacao = () => {
    saveVerificacao({
      id: verificacao?.id,
      formulario_plano_acao_id: planoAcaoId,
      eficaz,
      criterios_avaliacao: criteriosAvaliacao || null,
      resultado_obtido: resultadoObtido || null,
      acoes_adicionais: acoesAdicionais || null,
      observacoes: observacoesVerificacao || null,
      verificado_por: user?.id || null,
    });
  };

  const handleCreateLicao = () => {
    if (!novaLicao.titulo.trim()) return;
    
    createLicao({
      formulario_plano_acao_id: planoAcaoId,
      tipo: novaLicao.tipo,
      titulo: novaLicao.titulo,
      descricao: novaLicao.descricao || null,
      impacto: novaLicao.impacto || null,
      recomendacoes: novaLicao.recomendacoes || null,
      registrado_por: user?.id || null,
    });

    // Reset form
    setNovaLicao({
      tipo: 'melhoria',
      titulo: '',
      descricao: '',
      impacto: '',
      recomendacoes: '',
    });
    setShowNovaLicaoForm(false);
  };

  const handleDeleteLicao = (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta lição aprendida?')) {
      deleteLicao(id);
    }
  };

  if (isLoadingVerificacao || isLoadingLicoes) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Verificação de Eficácia */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            Verificação de Eficácia
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Label className="text-base font-semibold">O plano de ação foi eficaz?</Label>
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant={eficaz === true ? 'default' : 'outline'}
                size="sm"
                disabled={readOnly}
                onClick={() => setEficaz(true)}
                className={eficaz === true ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Sim
              </Button>
              <Button
                type="button"
                variant={eficaz === false ? 'default' : 'outline'}
                size="sm"
                disabled={readOnly}
                onClick={() => setEficaz(false)}
                className={eficaz === false ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Não
              </Button>
            </div>
          </div>

          <div>
            <Label>Critérios de Avaliação</Label>
            <Textarea
              placeholder="Quais critérios foram usados para verificar a eficácia?"
              value={criteriosAvaliacao}
              onChange={(e) => setCriteriosAvaliacao(e.target.value)}
              disabled={readOnly}
              rows={3}
            />
          </div>

          <div>
            <Label>Resultado Obtido</Label>
            <Textarea
              placeholder="Qual foi o resultado observado após a implementação?"
              value={resultadoObtido}
              onChange={(e) => setResultadoObtido(e.target.value)}
              disabled={readOnly}
              rows={3}
            />
          </div>

          {eficaz === false && (
            <div>
              <Label>Ações Adicionais Necessárias</Label>
              <Textarea
                placeholder="Quais ações adicionais serão necessárias?"
                value={acoesAdicionais}
                onChange={(e) => setAcoesAdicionais(e.target.value)}
                disabled={readOnly}
                rows={3}
              />
            </div>
          )}

          <div>
            <Label>Observações</Label>
            <Textarea
              placeholder="Observações adicionais..."
              value={observacoesVerificacao}
              onChange={(e) => setObservacoesVerificacao(e.target.value)}
              disabled={readOnly}
              rows={2}
            />
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button onClick={handleSaveVerificacao} disabled={isSavingVerificacao}>
                {isSavingVerificacao ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar Verificação'
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lições Aprendidas */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Lições Aprendidas
            </CardTitle>
            {!readOnly && (
              <Button size="sm" onClick={() => setShowNovaLicaoForm(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Nova Lição
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Form para nova lição */}
          {showNovaLicaoForm && (
            <Card className="border-dashed border-2">
              <CardContent className="pt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo *</Label>
                    <Select
                      value={novaLicao.tipo}
                      onValueChange={(value) => setNovaLicao({ ...novaLicao, tipo: value as any })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sucesso">Sucesso</SelectItem>
                        <SelectItem value="melhoria">Melhoria</SelectItem>
                        <SelectItem value="falha">Falha</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Título *</Label>
                    <Input
                      placeholder="Título da lição"
                      value={novaLicao.titulo}
                      onChange={(e) => setNovaLicao({ ...novaLicao, titulo: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Descrição</Label>
                  <Textarea
                    placeholder="Descreva a lição aprendida..."
                    value={novaLicao.descricao}
                    onChange={(e) => setNovaLicao({ ...novaLicao, descricao: e.target.value })}
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Impacto</Label>
                  <Textarea
                    placeholder="Qual foi o impacto desta lição?"
                    value={novaLicao.impacto}
                    onChange={(e) => setNovaLicao({ ...novaLicao, impacto: e.target.value })}
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Recomendações</Label>
                  <Textarea
                    placeholder="Recomendações para o futuro..."
                    value={novaLicao.recomendacoes}
                    onChange={(e) => setNovaLicao({ ...novaLicao, recomendacoes: e.target.value })}
                    rows={2}
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowNovaLicaoForm(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleCreateLicao} disabled={isCreating || !novaLicao.titulo.trim()}>
                    {isCreating ? 'Salvando...' : 'Salvar Lição'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lista de lições */}
          {licoes.length === 0 && !showNovaLicaoForm ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma lição aprendida registrada ainda.
            </p>
          ) : (
            <div className="space-y-3">
              {licoes.map((licao) => (
                <Card key={licao.id} className="bg-muted/30">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className={TIPO_LICAO_COLORS[licao.tipo]}>
                            {TIPO_LICAO_LABELS[licao.tipo]}
                          </Badge>
                          <span className="font-semibold">{licao.titulo}</span>
                        </div>
                        {licao.descricao && (
                          <p className="text-sm text-muted-foreground">{licao.descricao}</p>
                        )}
                        {licao.impacto && (
                          <p className="text-sm"><strong>Impacto:</strong> {licao.impacto}</p>
                        )}
                        {licao.recomendacoes && (
                          <p className="text-sm"><strong>Recomendações:</strong> {licao.recomendacoes}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Registrado em {new Date(licao.criado_em).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      {!readOnly && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteLicao(licao.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
